/**
 * Audit Explorer — execution-event debugger.
 *
 * Filters (URL-backed so deep-links work):
 *   - actor (sub or partial email)
 *   - method (e.g. tool.invoke, server.create)
 *   - decision (allow / deny / error)
 *   - reason_code (prefix match)
 *   - correlation_id (exact)
 *   - time range (from / to, ISO)
 *   - limit (default 100)
 *
 * Two views:
 *   1. Default — list of events with filters above.
 *   2. Correlation view — when a correlation_id is in the URL, the page
 *      narrows to that single trace and shows events in chronological
 *      order, so an admin can follow a tool call end-to-end.
 *
 * Click any event to open the detail drawer; from there a "Show all
 * events with this correlation" button sets the correlation filter.
 */
import { useMemo, useState, type JSX } from 'react';
import { Filter, Fingerprint, Link2, RotateCcw } from 'lucide-react';

import { useAuditEvents, type AuditEventListItem } from '@/api/index.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { HelpCallout } from '@/components/console/HelpCallout.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { ExecutionDetail } from '@/components/console/drilldowns/ExecutionDetail.js';
import { useUrlParam } from '@/hooks/useUrlState.js';

interface AuditTableRow extends DataRow {
  id: string;
  time: string;
  actor: string;
  method: string;
  tool: string;
  vs: string;
  decision: string;
  reason: string;
  latency: string;
}

function pickStr(p: Record<string, unknown>, path: readonly string[]): string {
  let cur: unknown = p;
  for (const k of path) {
    if (cur && typeof cur === 'object') cur = (cur as Record<string, unknown>)[k];
    else return '';
  }
  return cur === undefined || cur === null ? '' : String(cur);
}

function eventToRow(ev: AuditEventListItem): AuditTableRow {
  const p = ev.payload;
  // The audit-event payload schema (see packages/domain/src/audit) places
  // these under nested objects, not at the root. Earlier code was reading
  // `decision.effect` / root `method` — both wrong, which is why those
  // columns rendered as "—" or blank in the UI today.
  const decision = pickStr(p, ['decision', 'outcome']) || pickStr(p, ['decision', 'effect']) || '—';
  const method = pickStr(p, ['request', 'method']) || pickStr(p, ['method']) || ev.event_type;
  const tool = pickStr(p, ['resource', 'tool_name']);
  const vs = pickStr(p, ['resource', 'virtual_server_slug']);
  const latencyMs = pickStr(p, ['response', 'latency_total_ms']);
  return {
    id: ev.event_id.slice(-8),
    time: new Date(ev.created_at).toLocaleString(),
    actor: pickStr(p, ['actor', 'id']) || '—',
    method,
    tool: tool || '—',
    vs: vs || '—',
    decision,
    reason: pickStr(p, ['decision', 'reason_code']) || '—',
    latency: latencyMs ? `${latencyMs} ms` : '—',
  };
}

const DECISIONS = ['', 'allow', 'deny', 'error'] as const;

export default function AuditExplorer(): JSX.Element {
  // URL-backed filter state.
  const [actor, setActor] = useUrlParam('actor', '');
  const [method, setMethod] = useUrlParam('method', '');
  const [decision, setDecision] = useUrlParam('decision', '');
  const [reason, setReason] = useUrlParam('reason', '');
  const [correlation, setCorrelation] = useUrlParam('correlation', '');
  const [fromTs, setFromTs] = useUrlParam('from', '');
  const [toTs, setToTs] = useUrlParam('to', '');
  const [limit, setLimit] = useUrlParam('limit', '100');

  // Drawer selection.
  const [selectedParam, setSelectedParam] = useUrlParam('event', '');

  // Local filter form state — committed to URL on Apply, so typing
  // doesn't fire a query per keystroke.
  const [draft, setDraft] = useState({
    actor,
    method,
    decision,
    reason,
    correlation,
    fromTs,
    toTs,
    limit,
  });

  const events = useAuditEvents({
    actor: actor || undefined,
    method: method || undefined,
    decision: (decision as 'allow' | 'deny' | 'error') || undefined,
    reason: reason || undefined,
    correlation_id: correlation || undefined,
    from_ts: fromTs || undefined,
    to_ts: toTs || undefined,
    limit: limit ? Number(limit) || 100 : 100,
  });

  const rows = useMemo(() => (events.data?.items ?? []).map(eventToRow), [events.data?.items]);

  const selected = useMemo(
    () => events.data?.items.find((e) => e.event_id.slice(-8) === selectedParam) ?? null,
    [events.data?.items, selectedParam],
  );

  function applyFilters(): void {
    setActor(draft.actor);
    setMethod(draft.method);
    setDecision(draft.decision);
    setReason(draft.reason);
    setCorrelation(draft.correlation);
    setFromTs(draft.fromTs);
    setToTs(draft.toTs);
    setLimit(draft.limit);
  }

  function resetFilters(): void {
    setDraft({
      actor: '',
      method: '',
      decision: '',
      reason: '',
      correlation: '',
      fromTs: '',
      toTs: '',
      limit: '100',
    });
    setActor('');
    setMethod('');
    setDecision('');
    setReason('');
    setCorrelation('');
    setFromTs('');
    setToTs('');
    setLimit('100');
  }

  const inCorrelationView = Boolean(correlation);

  return (
    <>
      <PageHeader
        eyebrow="Govern"
        title={inCorrelationView ? `Audit — correlation ${correlation}` : 'Audit Explorer'}
        description={
          inCorrelationView
            ? 'All events sharing this correlation ID, chronological. End-to-end trace for one request.'
            : 'Every write event the gateway emitted: who, what, when, decision. Filter, drill down, follow correlations.'
        }
      />

      <HelpCallout
        title="How to trace a tool call"
        storageKey="audit-help"
        whatHappens={
          <>
            Every write the gateway emits (tool calls, server CRUD, approval decisions, credential
            rotations) lands here as one event. Each event carries a <strong>correlation ID</strong>{' '}
            shared by every step of the same request — that's how you follow a single tool call from
            auth through to upstream response.
          </>
        }
        youNeed={[
          <>
            To filter, type into the form below and click <strong>Apply</strong>.
          </>,
          <>
            To trace one call, find any event from it and click <strong>Show correlation</strong> in
            the drawer.
          </>,
        ]}
        afterwards={[
          <>
            Bookmark the URL — every filter is in the query string, so the filtered view is
            shareable.
          </>,
        ]}
      />

      <SectionCard
        icon={Filter}
        title="Filters"
        text="All filters are AND'd. Leave a field blank to ignore it. Time fields accept ISO timestamps."
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 8,
          }}
        >
          <FilterField
            label="Actor (sub / email)"
            value={draft.actor}
            onChange={(v) => setDraft({ ...draft, actor: v })}
          />
          <FilterField
            label="Method"
            value={draft.method}
            placeholder="e.g. tool.invoke, server.create"
            onChange={(v) => setDraft({ ...draft, method: v })}
          />
          <FilterField
            label="Reason code (prefix)"
            value={draft.reason}
            onChange={(v) => setDraft({ ...draft, reason: v })}
          />
          <label style={{ display: 'block', fontSize: 13 }}>
            <span style={{ display: 'block', marginBottom: 4 }}>Decision</span>
            <select
              value={draft.decision}
              onChange={(e) => setDraft({ ...draft, decision: e.target.value })}
              style={inputStyle}
            >
              {DECISIONS.map((d) => (
                <option key={d} value={d}>
                  {d || 'any'}
                </option>
              ))}
            </select>
          </label>
          <FilterField
            label="Correlation ID"
            value={draft.correlation}
            placeholder="exact match"
            onChange={(v) => setDraft({ ...draft, correlation: v })}
          />
          <FilterField
            label="From (ISO)"
            value={draft.fromTs}
            placeholder="2026-05-01T00:00:00Z"
            onChange={(v) => setDraft({ ...draft, fromTs: v })}
          />
          <FilterField
            label="To (ISO)"
            value={draft.toTs}
            placeholder="2026-05-31T23:59:59Z"
            onChange={(v) => setDraft({ ...draft, toTs: v })}
          />
          <FilterField
            label="Limit"
            value={draft.limit}
            placeholder="100"
            onChange={(v) => setDraft({ ...draft, limit: v.replace(/[^0-9]/g, '') })}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button type="button" onClick={applyFilters} style={btnPrimary}>
            <Filter size={13} style={{ marginRight: 6 }} />
            Apply
          </button>
          <button type="button" onClick={resetFilters} style={btnSecondary}>
            <RotateCcw size={13} style={{ marginRight: 6 }} />
            Reset
          </button>
          {inCorrelationView && (
            <button
              type="button"
              onClick={() => {
                setDraft({ ...draft, correlation: '' });
                setCorrelation('');
              }}
              style={btnSecondary}
            >
              Clear correlation filter
            </button>
          )}
        </div>
      </SectionCard>

      {events.isError && (
        <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
          Audit-events API unreachable. Hard-refresh; if it persists the gateway is down.
        </p>
      )}
      {events.data && (
        <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
          {rows.length} event{rows.length === 1 ? '' : 's'} match.
        </p>
      )}

      <SectionCard
        icon={Fingerprint}
        title="Events"
        text="Click a row to open the detail drawer. From there, jump to a correlation trace."
      >
        <DataTable
          columns={['time', 'actor', 'method', 'tool', 'vs', 'decision', 'reason', 'latency']}
          rows={rows as readonly DataRow[]}
          onRow={(r) => setSelectedParam(String(r.id))}
        />
      </SectionCard>

      <ExecutionDetail
        open={selected != null}
        onClose={() => setSelectedParam('')}
        event={selected}
        extraActions={
          selected ? (
            <button
              type="button"
              onClick={() => {
                setDraft({ ...draft, correlation: selected.correlation_id });
                setCorrelation(selected.correlation_id);
                setSelectedParam('');
              }}
              style={btnSecondary}
            >
              <Link2 size={13} style={{ marginRight: 6 }} />
              Show all events with this correlation
            </button>
          ) : null
        }
      />
    </>
  );
}

/* ── helpers ──────────────────────────────────────────────────────── */

function FilterField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label style={{ display: 'block', fontSize: 13 }}>
      <span style={{ display: 'block', marginBottom: 4 }}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};
const btnPrimary: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid rgba(140, 21, 21, 0.3)',
  background: 'linear-gradient(135deg, #8c1515, #b83a24)',
  color: '#fff8ec',
  fontWeight: 650,
  cursor: 'pointer',
  fontSize: 13,
  display: 'inline-flex',
  alignItems: 'center',
};
const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--line-strong)',
  background: 'rgba(255,255,255,0.85)',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};
